<template>
    <div class="row g-3 align-items-center mb-3">
        <div class="col-auto">
            <InputField colField="mb-0" :editable="true" label="Month" :options="monthOptions" type="select"
                :modelValue="mcpUsageMonth" @change="usageMonthChange($event)" />
        </div>
        <div class="col-auto">
            <InputField label="Start Date" colField="mb-0" :modelValue="mcpStartDate" type="calendar"
                :input-config="dateConfig" @update:modelValue="startDateChange($event)" />
        </div>
        <div class="col-auto">
            <InputField label="End Date" colField="mb-0" :modelValue="mcpEndDate" type="calendar"
                :input-config="dateConfig" @update:modelValue="endDateChange($event)" />
        </div>
        <!-- Status + user filters: shared across all tabs -->
        <div class="col-auto">
            <InputField label="Status" colField="mb-0" v-model="selectedStatus" type="select"
                :options="statusOptions" />
        </div>
        <div class="col-auto" v-if="hasUserData">
            <InputField label="User" colField="mb-0" v-model="selectedUser" type="select" :options="userOptions" />
        </div>
    </div>

    <Tabs :tabs="tabItems">

        <!-- ───── DAILY TAB ───── -->
        <template v-slot:daily>
            <div class="card">
                <div class="card-body">
                    <div v-if="mcpLoading" class="text-center py-2">
                        <div class="spinner-border mb-2" role="status" style="width: 1.5em; height: 1.5em; opacity: 0.5;">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <p class="text-muted mb-0">Loading daily data...</p>
                    </div>
                    <template v-else>
                        <div v-if="filteredDailyRows.length === 0" class="text-center py-2">
                            <div class="text-muted mb-2">
                                <i class="fa-solid fa-circle-exclamation" style="font-size: 1.5rem; opacity: 0.5;"></i>
                            </div>
                            <p class="text-muted mb-0">No data available for the selected period</p>
                        </div>
                        <div v-else>
                            <div class="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                                <!-- Window controls -->
                                <div class="d-flex align-items-center gap-2">
                                    <label class="form-label mb-0 text-muted"
                                        style="white-space:nowrap; font-size:0.85rem;">Days shown</label>
                                    <select class="form-select form-select-sm" style="width:75px;"
                                        v-model.number="dailyWindowSize">
                                        <option :value="7">7</option>
                                        <option :value="14">14</option>
                                        <option :value="30">30</option>
                                        <option :value="allDates.length">All</option>
                                    </select>
                                    <div class="btn-group btn-group-sm">
                                        <button class="btn btn-outline-secondary" :disabled="dailyWindowOffset === 0"
                                            @click="dailyWindowOffset = Math.max(0, dailyWindowOffset - dailyWindowSize)">
                                            <i class="fa fa-chevron-left"></i>
                                        </button>
                                        <button class="btn btn-outline-secondary"
                                            :disabled="dailyWindowOffset + dailyWindowSize >= allDates.length"
                                            @click="dailyWindowOffset = Math.min(allDates.length - dailyWindowSize, dailyWindowOffset + dailyWindowSize)">
                                            <i class="fa fa-chevron-right"></i>
                                        </button>
                                    </div>
                                    <span class="text-muted" style="font-size:0.8rem; white-space:nowrap;">{{
                                        windowLabel
                                        }}</span>
                                </div>

                                <!-- Operation type filter pills -->
                                <div class="d-flex flex-wrap gap-2">
                                    <span v-for="key in allOperationKeys" :key="key" class="badge rounded-pill"
                                        :style="keyPillStyle(key)"
                                        style="cursor:pointer; user-select:none; font-size:0.8rem; padding:0.4em 0.85em;"
                                        @click="toggleKey(key)">{{ key }}</span>
                                </div>
                            </div>

                            <!-- Stacked bar: x=date, datasets=operation keys -->
                            <div class="chart-view">
                                <Bar :data="dailyChartData" :options="dailyChartOptions" />
                            </div>

                            <!-- Detail table: top targets for the windowed period -->
                            <div class="mt-4">
                                <p class="text-muted mb-2" style="font-size:0.85rem;">
                                    Top targets in view
                                    <span v-if="selectedStatus" class="badge bg-secondary ms-1">{{ selectedStatus
                                        }}</span>
                                </p>
                                <div class="agg-table-wrap">
                                    <table class="table table-sm table-hover mb-0">
                                        <thead class="table-light sticky-top">
                                            <tr>
                                                <th style="width:30%">Operation</th>
                                                <th style="width:40%">Target</th>
                                                <th style="width:15%">Status</th>
                                                <th class="text-end" style="width:15%">Count</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="row in dailyTargetSummary"
                                                :key="`${row.key}-${row.target}-${row.subLabel}`">
                                                <td>
                                                    <span class="badge text-white"
                                                        :style="`background:${keyColor(row.key)};`">
                                                        {{ row.key }}
                                                    </span>
                                                </td>
                                                <td class="agg-cell-truncate" :title="row.target">{{ row.target }}</td>
                                                <td>
                                                    <span :class="[statusBadgeClass(row.subLabel)]"
                                                        :style="{ color: `${this.$store.state.darkMode ? '#000' : '#fff'}` }">{{
                                                            row.subLabel }}</span>
                                                </td>
                                                <td class="text-end fw-semibold">{{ formatNumber(row.count) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </template>

        <!-- ───── AGGREGATE TAB ───── -->
        <template v-slot:aggregate>
            <div class="card">
                <div class="card-body">
                    <div v-if="mcpLoading" class="text-center py-2">
                        <div class="spinner-border mb-2" role="status" style="width: 1.5em; height: 1.5em; opacity: 0.5;">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <p class="text-muted mb-0">Loading aggregated data...</p>
                    </div>
                    <template v-else>
                        <div v-if="filteredAggregateRows.length === 0" class="text-center py-2">
                            <div class="text-muted mb-2">
                                <i class="fa-solid fa-circle-exclamation" style="font-size: 1.5rem; opacity: 0.5;"></i>
                            </div>
                            <p class="text-muted mb-0">No data available for the selected period</p>
                        </div>
                        <div v-else class="row g-4">

                            <!-- Right: summary table -->
                            <div class="col-6">
                                <p class="text-muted mb-2" style="font-size:0.85rem;">Full breakdown</p>
                                <div class="agg-table-wrap">
                                    <table class="table table-sm mb-0">
                                        <thead class="table-light sticky-top">
                                            <tr>
                                                <th style="width:35%">Operation</th>
                                                <th style="width:35%">Target</th>
                                                <!-- <th class="text-end" style="width:15%">Count</th> -->
                                                <th style="width:15%">Status</th>
                                                <th class="text-end" style="width:15%">Count</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <template v-for="group in aggTableData" :key="group.key">
                                                <tr v-for="(row, i) in group.rows"
                                                    :key="`${row.target}-${row.subLabel}`">
                                                    <td v-if="i === 0" :rowspan="group.rows.length + 1"
                                                        class="fw-semibold align-middle agg-cell-truncate"
                                                        :title="group.key">
                                                        <span class="badge text-white"
                                                            :style="`background:${keyColor(group.key)}; font-size:0.7rem;`">
                                                            {{ group.key }}
                                                        </span>
                                                    </td>
                                                    <td class="agg-cell-truncate" :title="row.target">{{ row.target }}
                                                    </td>
                                                    <td>
                                                        <span
                                                            :class="[statusBadgeClass(row.subLabel)]" :style="{color: `${this.$store.state.darkMode ? '#000' : '#fff'}`}">{{
                                                                row.subLabel }}</span>
                                                    </td>
                                                    <td class="text-end">{{ formatNumber(row.count) }}</td>
                                                </tr>
                                                <!-- subtotal -->
                                                <tr class="subtotal-row">
                                                    <td class="fst-italic text-muted" style="font-size:0.8rem;"
                                                        colspan="2">subtotal</td>
                                                    <td class="text-end fw-bold" style="font-size:0.8rem;">{{
                                                        formatNumber(group.total) }}</td>
                                                </tr>
                                            </template>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <!-- Left: donut chart — operation type distribution -->
                            <div class="col-6">
                                <div class="mb-4">
                                    <p class="text-muted mb-2" style="font-size:0.85rem;">Calls by operation type</p>
                                    <div style="position:relative; height:260px;">
                                        <Doughnut :data="aggDonutData" :options="aggDonutOptions" />
                                    </div>
                                </div>
                                <div>
                                    <p class="text-muted mb-2" style="font-size:0.85rem;">Top targets by call count</p>
                                    <div :style="`position:relative; height:${aggBarHeight}px;`">
                                        <Bar :data="aggTargetBarData" :options="aggTargetBarOptions" />
                                    </div>
                                </div>
                            </div>

                            <!-- Middle: horizontal bar — top targets -->

                        </div>
                    </template>
                </div>
            </div>
        </template>

        <!-- ───── BY USER TAB ───── -->
        <template v-slot:byUser>
            <div class="card">
                <div class="card-body">
                    <div v-if="mcpLoading" class="text-center py-2">
                        <div class="spinner-border mb-2" role="status" style="width: 1.5em; height: 1.5em; opacity: 0.5;">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <p class="text-muted mb-0">Loading per-user data...</p>
                    </div>
                    <template v-else>
                        <div v-if="userSummary.length === 0" class="text-center py-2">
                            <div class="text-muted mb-2">
                                <i class="fa-solid fa-circle-exclamation" style="font-size: 1.5rem; opacity: 0.5;"></i>
                            </div>
                            <p class="text-muted mb-0">No data available for the selected period</p>
                        </div>
                        <div v-else>
                            <!-- Controls -->
                            <div class="d-flex flex-wrap align-items-center justify-content-end gap-3 mb-3">
                                <div class="d-flex align-items-center gap-2">
                                    <label class="form-label mb-0 text-muted"
                                        style="white-space:nowrap; font-size:0.85rem;">Users charted</label>
                                    <select class="form-select form-select-sm" style="width:80px;"
                                        v-model.number="userTopN">
                                        <option :value="5">5</option>
                                        <option :value="10">10</option>
                                        <option :value="25">25</option>
                                        <option :value="namedUsers.length">All</option>
                                    </select>
                                </div>
                            </div>

                            <div class="row g-4">
                                <!-- Leaderboard -->
                                <div class="col-lg-6">
                                    <p class="text-muted mb-2" style="font-size:0.85rem;">Calls by user</p>
                                    <div class="agg-table-wrap">
                                        <table class="table table-sm table-hover mb-0">
                                            <thead class="table-light sticky-top">
                                                <tr>
                                                    <th style="width:40%">User</th>
                                                    <th class="text-end" style="width:17%">Operations</th>
                                                    <th class="text-end" style="width:15%">Targets</th>
                                                    <th class="text-end" style="width:15%">Calls</th>
                                                    <th class="text-end" style="width:13%">Share</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="row in userSummary" :key="row.user"
                                                    :class="{ 'unattributed-row': isUnattributed(row.user) }">
                                                    <td class="agg-cell-truncate" :title="userLabel(row.user)">
                                                        <i class="fa fa-circle me-1"
                                                            :style="`color:${userColor(row.user)}; font-size:0.6rem; vertical-align:middle;`"></i>
                                                        <span :class="{ 'fst-italic text-muted': isUnattributed(row.user) }">
                                                            {{ userLabel(row.user) }}
                                                        </span>
                                                    </td>
                                                    <td class="text-end" :title="row.operationList">
                                                        {{ row.operationCount }}
                                                    </td>
                                                    <td class="text-end">{{ row.targetCount }}</td>
                                                    <td class="text-end fw-semibold">{{ formatNumber(row.count) }}</td>
                                                    <td class="text-end text-muted" style="font-size:0.8rem;">
                                                        {{ userShare(row.count).toFixed(1) }}%
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <!-- Top users, split by operation type -->
                                <div class="col-lg-6">
                                    <p class="text-muted mb-2" style="font-size:0.85rem;">
                                        Top users by operation type
                                        <span v-if="namedUsers.length > topUsers.length" class="text-muted">
                                            (showing {{ topUsers.length }} of {{ namedUsers.length }})
                                        </span>
                                    </p>
                                    <div v-if="topUsers.length === 0" class="text-muted py-4 text-center"
                                        style="font-size:0.85rem;">
                                        No attributed usage in this period
                                    </div>
                                    <div v-else :style="`position:relative; height:${userBarHeight}px;`">
                                        <Bar :data="userBarData" :options="userBarOptions" />
                                    </div>
                                </div>
                            </div>

                            <!-- Daily trend stacked by user -->
                            <div class="mt-4">
                                <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                                    <p class="text-muted mb-0" style="font-size:0.85rem;">Daily calls by user</p>
                                    <div class="d-flex align-items-center gap-2">
                                        <label class="form-label mb-0 text-muted"
                                            style="white-space:nowrap; font-size:0.85rem;">Days shown</label>
                                        <select class="form-select form-select-sm" style="width:75px;"
                                            v-model.number="dailyWindowSize">
                                            <option :value="7">7</option>
                                            <option :value="14">14</option>
                                            <option :value="30">30</option>
                                            <option :value="allDates.length">All</option>
                                        </select>
                                        <div class="btn-group btn-group-sm">
                                            <button class="btn btn-outline-secondary" :disabled="dailyWindowOffset === 0"
                                                @click="dailyWindowOffset = Math.max(0, dailyWindowOffset - dailyWindowSize)">
                                                <i class="fa fa-chevron-left"></i>
                                            </button>
                                            <button class="btn btn-outline-secondary"
                                                :disabled="dailyWindowOffset + dailyWindowSize >= allDates.length"
                                                @click="dailyWindowOffset = Math.min(allDates.length - dailyWindowSize, dailyWindowOffset + dailyWindowSize)">
                                                <i class="fa fa-chevron-right"></i>
                                            </button>
                                        </div>
                                        <span class="text-muted" style="font-size:0.8rem; white-space:nowrap;">
                                            {{ windowLabel }}
                                        </span>
                                    </div>
                                </div>
                                <div class="chart-view">
                                    <Bar :data="userDailyChartData" :options="userDailyChartOptions" />
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </template>
    </Tabs>
</template>

<script>
import InputField from '../../components/InputField.vue';
import { generateLastSixMonthOptions, getFirstDayOfMonth, getLastDayOfMonth, formatNumber } from '../../utils/commonFunction';
import Tabs from "../../components/Tabs.vue";
import { mapState } from "vuex";
import { Bar, Doughnut } from 'vue-chartjs';
import {
    Chart as ChartJS, Title, Tooltip, Legend,
    BarElement, ArcElement, CategoryScale, LinearScale
} from 'chart.js';

ChartJS.register(Title, Tooltip, Legend, BarElement, ArcElement, CategoryScale, LinearScale);

const KEY_COLORS = [
    '#4e79a7', // steel blue
    '#e15759', // coral red
    '#f28e2b', // orange
    '#59a14f', // green
    '#b07aa1', // purple
    '#76b7b2', // teal
    '#ff9da7', // pink
    '#9c755f', // brown
    '#edc948', // yellow  ← still here but only used if >8 models; not as opacity overlay
    '#7f3c8d', // violet
    '#11a579', // jade
    '#3969ac', // indigo
    '#f2b701', // amber
    '#e73f74', // magenta
    '#80ba5a', // moss
    '#008695', // deep cyan
    '#cf1c90', // fuchsia
    '#5f4690', // dark purple
    '#1d6996', // ocean blue
    '#94346e', // plum
];

// A null `user` is usage recorded before per-user attribution existed, so it is a
// peer bucket alongside the named users — never a rollup of them. Collapse it to a
// sentinel so it can be keyed and filtered. `idx('user')` is -1 on responses that
// predate the field, which lands every row here — the by-user tab then hides itself.
const UNATTRIBUTED = '__unattributed__';
const UNATTRIBUTED_COLOR = '#adb5bd';
function userKey(user) {
    return user || UNATTRIBUTED;
}
function userLabel(user) {
    if (!user || user === UNATTRIBUTED) return 'Unattributed';
    return user;
}
/** Emails are too long for an axis tick — local part only, full address in tooltips. */
function userShortLabel(user) {
    if (!user || user === UNATTRIBUTED) return 'Unattributed';
    return String(user).split('@')[0];
}
/** Named users before Unattributed, which always sorts last. */
function compareUsers(a, b) {
    if (a === b) return 0;
    if (a === UNATTRIBUTED) return 1;
    if (b === UNATTRIBUTED) return -1;
    return a < b ? -1 : 1;
}

function compactTick(v) {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
    return v;
}

export default {
    name: 'AiUsageMcp',
    components: { InputField, Tabs, Bar, Doughnut },
    props: ['mcpLoading'],
    emits: ['getUsageData'],

    data() {
        return {
            dateConfig: { wrap: true, dateFormat: 'Y-m-d' },
            monthOptions: generateLastSixMonthOptions(),

            // Shared filters
            selectedStatus: '',       // '' = All
            selectedUser: '',         // '' = all users; applies to every tab

            // By-user tab
            userTopN: 10,

            // Daily controls
            dailyWindowSize: 14,
            dailyWindowOffset: 0,
            selectedKeys: [],    // operation type pills
        };
    },

    computed: {
        ...mapState({
            mcpStartDate: state => state.admin.mcpUsageDataStartDate,
            mcpEndDate: state => state.admin.mcpUsageDataEndDate,
            mcpUsageMonth: state => state.admin.mcpUsageMonth,
            mcpDataDaily: state => state.admin.mcpUsageData.daily ?? null,
            mcpDataAggregate: state => state.admin.mcpUsageData.aggregate ?? null,
        }),

        tabItems() {
            const tabs = [
                { name: "Daily", id: "id1", slot: "daily", loading: this.mcpLoading },
                { name: "Aggregate", id: "id2", slot: "aggregate", loading: this.mcpLoading },
            ];
            // Nothing to show if the range predates user attribution entirely.
            if (this.hasUserData) {
                tabs.push({ name: "By User", id: "id3", slot: "byUser", loading: this.mcpLoading });
            }
            return tabs;
        },

        // ── Shared ────────────────────────────────────────────────────────────

        /** False when the whole range predates attribution — hides the filter + tab. */
        hasUserData() {
            return this.dailyRows.some(r => r.user !== UNATTRIBUTED)
                || this.aggregateRows.some(r => r.user !== UNATTRIBUTED);
        },

        allUsers() {
            const users = new Set([
                ...this.dailyRows.map(r => r.user),
                ...this.aggregateRows.map(r => r.user),
            ]);
            return [...users].sort(compareUsers);
        },

        userOptions() {
            return [
                { label: 'All users', value: '' },
                ...this.allUsers.map(u => ({ label: userLabel(u), value: u })),
            ];
        },

        userColorMap() {
            const map = {};
            let i = 0;
            for (const u of this.allUsers) {
                if (u === UNATTRIBUTED) {
                    map[u] = UNATTRIBUTED_COLOR;
                } else {
                    map[u] = KEY_COLORS[i++ % KEY_COLORS.length];
                }
            }
            return map;
        },

        /** All unique statuses from both daily and aggregate — drives the dropdown */
        statusOptions() {
            const daily = this.dailyRows.map(r => r.subLabel);
            const agg = this.aggregateRows.map(r => r.subLabel);
            const allStatuses = [...new Set([...daily, ...agg])].sort();
            let options = allStatuses.map(s => ({ label: s, value: s }));
            options.unshift({ label: 'All', value: '' });
            return options;
        },

        // ── Daily ─────────────────────────────────────────────────────────────

        dailyRows() {
            if (!this.mcpDataDaily?.data) return [];
            const { headers, data } = this.mcpDataDaily;
            const idx = f => headers.indexOf(f);
            return data.map(row => ({
                date: row[idx('date')],
                key: row[idx('key')],
                target: row[idx('target')],
                user: userKey(row[idx('user')]),
                count: row[idx('count')] ?? 0,
                subLabel: row[idx('sub_label')],
            }));
        },

        /** Rows narrowed by both header filters */
        filteredDailyRows() {
            return this.dailyRows.filter(r =>
                (!this.selectedStatus || r.subLabel === this.selectedStatus)
                && (!this.selectedUser || r.user === this.selectedUser)
            );
        },

        allOperationKeys() {
            return [...new Set(this.dailyRows.map(r => r.key))].sort();
        },

        /** Stable color map: operation key → color */
        keyColorMap() {
            const map = {};
            this.allOperationKeys.forEach((k, i) => {
                map[k] = KEY_COLORS[i % KEY_COLORS.length];
            });
            return map;
        },

        allDates() {
            return [...new Set(this.dailyRows.map(r => r.date))].sort();
        },

        windowedDates() {
            return this.allDates.slice(
                this.dailyWindowOffset,
                this.dailyWindowOffset + this.dailyWindowSize
            );
        },

        windowLabel() {
            const d = this.windowedDates;
            if (!d.length) return '';
            return d.length === 1 ? d[0] : `${d[0]} – ${d[d.length - 1]}`;
        },

        /**
         * Daily stacked bar: x=date, one dataset per operation key.
         * Counts summed across targets (target detail visible in table below).
         */
        dailyChartData() {
            const dates = this.windowedDates;
            const activeKeys = this.selectedKeys;
            const rows = this.filteredDailyRows.filter(r =>
                activeKeys.includes(r.key) && dates.includes(r.date)
            );

            const datasets = activeKeys.map(key => {
                const color = this.keyColorMap[key] ?? '#888';
                const dateValues = {};
                rows.filter(r => r.key === key).forEach(r => {
                    dateValues[r.date] = (dateValues[r.date] ?? 0) + r.count;
                });
                return {
                    label: key,
                    data: dates.map(d => dateValues[d] ?? 0),
                    backgroundColor: color + 'cc',
                    borderColor: '#ffffff00',
                    borderWidth: {
                        top: 2,
                        right: 0,
                        bottom: 0,
                        left: 0
                    },
                    stack: 'ops',
                };
            });

            return { labels: dates, datasets };
        },

        dailyChartOptions() {
            const gridColor = this.$store.state.darkMode ? '#2e3238' : '#dee2e6';
            const textColor = this.$store.state.darkMode ? '#dee2e6' : '#212529';
            return {
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    colors: { enabled: false },
                    legend: { position: 'bottom', labels: { font: { size: 11 }, boxWidth: 12, padding: 10, color: textColor } },
                    tooltip: {
                        callbacks: {
                            label: ctx => ` ${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString()} calls`,
                        },
                    },
                },
                scales: {
                    x: { stacked: true, title: { display: true, text: 'Date' }, ticks: { maxRotation: 45, autoSkip: false }, grid: { color: gridColor, drawOnChartArea: true } },
                    y: { stacked: true, title: { display: true, text: 'Call Count' }, ticks: { callback: compactTick }, grid: { color: gridColor, drawOnChartArea: true } },
                },
            };
        },

        /** Target breakdown table for the windowed period */
        dailyTargetSummary() {
            const dates = this.windowedDates;
            const rows = this.filteredDailyRows.filter(r =>
                this.selectedKeys.includes(r.key) && dates.includes(r.date)
            );
            // Sum count per (key, target, subLabel)
            const acc = {};
            for (const r of rows) {
                const k = `${r.key}||${r.target}||${r.subLabel}`;
                if (!acc[k]) acc[k] = { key: r.key, target: r.target, subLabel: r.subLabel, count: 0 };
                acc[k].count += r.count;
            }
            return Object.values(acc).sort((a, b) => b.count - a.count);
        },

        // ── Aggregate ─────────────────────────────────────────────────────────

        aggregateRows() {
            if (!this.mcpDataAggregate?.data) return [];
            const { headers, data } = this.mcpDataAggregate;
            const idx = f => headers.indexOf(f);
            return data.map(row => ({
                key: row[idx('key')],
                target: row[idx('target')],
                user: userKey(row[idx('user')]),
                count: row[idx('count')] ?? 0,
                subLabel: row[idx('sub_label')],
            }));
        },

        filteredAggregateRows() {
            return this.aggregateRows.filter(r =>
                (!this.selectedStatus || r.subLabel === this.selectedStatus)
                && (!this.selectedUser || r.user === this.selectedUser)
            );
        },

        /** Donut: total calls per operation key */
        aggDonutData() {
            const rows = this.filteredAggregateRows;
            const keys = [...new Set(rows.map(r => r.key))].sort();
            return {
                labels: keys,
                datasets: [{
                    data: keys.map(k => rows.filter(r => r.key === k).reduce((s, r) => s + r.count, 0)),
                    backgroundColor: keys.map(k => (this.keyColorMap[k] ?? '#888') + 'cc'),
                    borderColor: keys.map(k => this.keyColorMap[k] ?? '#888'),
                    borderWidth: 1
                }],
            };
        },

        aggDonutOptions() {
            const textColor = this.$store.state.darkMode ? '#dee2e6' : '#212529';
            return {
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    colors: { enabled: false },
                    legend: { position: 'bottom', labels: { font: { size: 11 }, boxWidth: 12, padding: 8, color: textColor } },
                    tooltip: {
                        callbacks: {
                            label: ctx => ` ${ctx.label}: ${ctx.parsed.toLocaleString()} calls`,
                        },
                    },
                },
            };
        },

        /** Horizontal bar: top 10 targets by total count (excluding N/A) */
        aggTargetBarData() {
            const rows = this.filteredAggregateRows.filter(r => r.target !== 'N/A');
            const acc = {};
            for (const r of rows) {
                acc[r.target] = (acc[r.target] ?? 0) + r.count;
            }
            const sorted = Object.entries(acc)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 10);
            return {
                labels: sorted.map(([t]) => t),
                datasets: [{
                    label: 'Calls',
                    data: sorted.map(([, c]) => c),
                    backgroundColor: '#4e79a7cc',
                    borderColor: '#4e79a7',
                    borderWidth: 0.5,
                }],
            };
        },

        aggBarHeight() {
            const rows = this.filteredAggregateRows.filter(r => r.target !== 'N/A');
            const uniqueTargets = Math.min(new Set(rows.map(r => r.target)).size, 10);
            return Math.max(200, uniqueTargets * 40 + 60);
        },

        aggTargetBarOptions() {
            const gridColor = this.$store.state.darkMode ? '#2e3238' : '#dee2e6';
            return {
                indexAxis: 'y',
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    colors: { enabled: false },
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: ctx => ` ${ctx.parsed.x.toLocaleString()} calls`,
                        },
                    },
                },
                scales: {
                    x: { title: { display: true, text: 'Calls' }, ticks: { callback: compactTick }, grid: { color: gridColor, drawOnChartArea: true } },
                    y: { ticks: { font: { size: 11 } }, grid: { color: gridColor, drawOnChartArea: true } },
                },
            };
        },

        /** Full breakdown grouped by operation key for the table */
        aggTableData() {
            // The API now returns one row per (key, target, user, sub_label), so sum
            // across users to keep this table at one row per target+status.
            const groups = {};
            for (const r of this.filteredAggregateRows) {
                if (!groups[r.key]) groups[r.key] = { key: r.key, rowMap: {}, total: 0 };
                const group = groups[r.key];
                const rowKey = `${r.target}||${r.subLabel}`;
                if (!group.rowMap[rowKey]) {
                    group.rowMap[rowKey] = { target: r.target, subLabel: r.subLabel, count: 0 };
                }
                group.rowMap[rowKey].count += r.count;
                group.total += r.count;
            }
            return Object.values(groups)
                .map(g => ({
                    ...g,
                    rows: Object.values(g.rowMap).sort((a, b) => b.count - a.count),
                }))
                .sort((a, b) => b.total - a.total);
        },

        // ── By user ───────────────────────────────────────────────────────────

        /** One row per user for the whole period, built from the aggregate feed. */
        userSummary() {
            const acc = {};
            for (const r of this.filteredAggregateRows) {
                if (!acc[r.user]) {
                    acc[r.user] = {
                        user: r.user, operations: new Set(), targets: new Set(), count: 0,
                    };
                }
                const u = acc[r.user];
                u.operations.add(r.key);
                // 'N/A' is the placeholder for operations without a target — it would
                // otherwise inflate every user's target count by one.
                if (r.target && r.target !== 'N/A') u.targets.add(r.target);
                u.count += r.count;
            }
            return Object.values(acc)
                .map(u => ({
                    ...u,
                    operationCount: u.operations.size,
                    targetCount: u.targets.size,
                    operationList: [...u.operations].sort().join(', '),
                }))
                .sort((a, b) => {
                    // Unattributed last, named users by call volume.
                    if (a.user === UNATTRIBUTED) return 1;
                    if (b.user === UNATTRIBUTED) return -1;
                    return b.count - a.count;
                });
        },

        userGrandTotal() {
            return this.userSummary.reduce((sum, u) => sum + u.count, 0);
        },

        unattributedCalls() {
            const row = this.userSummary.find(u => u.user === UNATTRIBUTED);
            return row ? row.count : 0;
        },

        unattributedShare() {
            if (!this.userGrandTotal) return 0;
            return (this.unattributedCalls / this.userGrandTotal) * 100;
        },

        namedUsers() {
            return this.userSummary.filter(u => u.user !== UNATTRIBUTED);
        },

        topUsers() {
            return this.namedUsers.slice(0, this.userTopN);
        },

        /** Horizontal bar: top users, each bar split by operation type. */
        userBarData() {
            const users = this.topUsers.map(u => u.user);
            const rows = this.filteredAggregateRows.filter(r => users.includes(r.user));
            const keys = [...new Set(rows.map(r => r.key))].sort();

            const totals = {};
            for (const r of rows) {
                const k = `${r.user}||${r.key}`;
                totals[k] = (totals[k] ?? 0) + r.count;
            }

            return {
                labels: users.map(userShortLabel),
                datasets: keys.map(key => ({
                    label: key,
                    data: users.map(u => totals[`${u}||${key}`] ?? 0),
                    backgroundColor: (this.keyColorMap[key] ?? '#888') + 'cc',
                    borderColor: this.keyColorMap[key] ?? '#888',
                    borderWidth: 0.5,
                })),
            };
        },

        userBarHeight() {
            return Math.max(220, this.topUsers.length * 44 + 70);
        },

        userBarOptions() {
            const gridColor = this.$store.state.darkMode ? '#2e3238' : '#dee2e6';
            const textColor = this.$store.state.darkMode ? '#dee2e6' : '#212529';
            const fullNames = this.topUsers.map(u => userLabel(u.user));

            return {
                indexAxis: 'y',
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    colors: { enabled: false },
                    legend: {
                        position: 'bottom',
                        labels: { font: { size: 11 }, boxWidth: 12, padding: 10, color: textColor },
                    },
                    tooltip: {
                        callbacks: {
                            // Ticks are truncated to the local part; show the address here.
                            title: items => fullNames[items[0].dataIndex] ?? '',
                            label: ctx => ` ${ctx.dataset.label}: ${ctx.parsed.x.toLocaleString()} calls`,
                        },
                    },
                },
                scales: {
                    x: {
                        stacked: true,
                        title: { display: true, text: 'Calls' },
                        ticks: { callback: compactTick },
                        grid: { color: gridColor, drawOnChartArea: true },
                    },
                    y: {
                        stacked: true,
                        ticks: { font: { size: 11 } },
                        grid: { color: gridColor, drawOnChartArea: true },
                    },
                },
            };
        },

        /** Daily trend stacked by user, reusing the Daily tab's date window. */
        userDailyChartData() {
            const dates = this.windowedDates;
            const rows = this.filteredDailyRows.filter(r => dates.includes(r.date));
            const users = [...new Set(rows.map(r => r.user))].sort(compareUsers);

            const datasets = users.map(user => {
                const color = this.userColorMap[user] ?? '#888';
                const dateValues = {};
                rows.filter(r => r.user === user).forEach(r => {
                    dateValues[r.date] = (dateValues[r.date] ?? 0) + r.count;
                });
                return {
                    label: userShortLabel(user),
                    data: dates.map(d => dateValues[d] ?? 0),
                    backgroundColor: color + 'cc',
                    borderColor: '#ffffff00',
                    borderWidth: { top: 2, right: 0, bottom: 0, left: 0 },
                    stack: 'users',
                };
            });

            return { labels: dates, datasets };
        },

        userDailyChartOptions() {
            const gridColor = this.$store.state.darkMode ? '#2e3238' : '#dee2e6';
            const textColor = this.$store.state.darkMode ? '#dee2e6' : '#212529';
            return {
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    colors: { enabled: false },
                    legend: {
                        position: 'bottom',
                        labels: { font: { size: 11 }, boxWidth: 12, padding: 10, color: textColor },
                    },
                    tooltip: {
                        callbacks: {
                            label: ctx => ` ${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString()} calls`,
                        },
                    },
                },
                scales: {
                    x: { stacked: true, title: { display: true, text: 'Date' }, ticks: { maxRotation: 45, autoSkip: false }, grid: { color: gridColor, drawOnChartArea: true } },
                    y: { stacked: true, title: { display: true, text: 'Call Count' }, ticks: { callback: compactTick }, grid: { color: gridColor, drawOnChartArea: true } },
                },
            };
        },
    },

    watch: {
        allUsers(users) {
            // A date-range change can retire the selected user; falling back to All
            // beats silently rendering an empty chart.
            if (this.selectedUser && !users.includes(this.selectedUser)) {
                this.selectedUser = '';
            }
        },
        allOperationKeys: {
            immediate: true,
            handler(keys) {
                const existing = new Set(this.selectedKeys);
                if (!existing.size) {
                    this.selectedKeys = [...keys];
                } else {
                    const kept = keys.filter(k => existing.has(k));
                    const added = keys.filter(k => !existing.has(k));
                    this.selectedKeys = [...kept, ...added];
                }
            },
        },
        allDates(newDates) {
            this.dailyWindowOffset = Math.max(0, newDates.length - this.dailyWindowSize);
        },
        dailyWindowSize() {
            this.dailyWindowOffset = Math.min(
                this.dailyWindowOffset,
                Math.max(0, this.allDates.length - this.dailyWindowSize)
            );
        },
    },

    methods: {
        formatNumber,
        userLabel,
        userShortLabel,

        userColor(user) {
            return this.userColorMap[user] ?? '#6c757d';
        },

        userShare(count) {
            if (!this.userGrandTotal) return 0;
            return (count / this.userGrandTotal) * 100;
        },

        isUnattributed(user) {
            return user === UNATTRIBUTED;
        },

        keyColor(key) {
            return this.keyColorMap[key] ?? '#6c757d';
        },

        keyPillStyle(key) {
            const color = this.keyColorMap[key] ?? '#888';
            const active = this.selectedKeys.includes(key);
            return active
                ? `background:${color}; color:#fff; border:1.5px solid ${color};`
                : `background:transparent; color:${color}; border:1.5px solid ${color}; opacity:0.45;`;
        },

        toggleKey(key) {
            const idx = this.selectedKeys.indexOf(key);
            if (idx === -1) {
                this.selectedKeys.push(key);
            } else if (this.selectedKeys.length > 1) {
                this.selectedKeys.splice(idx, 1);
            }
        },

        /** Color-coded badge class based on status value */
        statusBadgeClass(status) {
            const map = {
                success: `badge bg-success`,
                error: `badge bg-danger`,
                failure: `badge bg-danger`,
                timeout: `badge bg-warning`,
                pending: `badge bg-secondary`,
            };
            return map[status?.toLowerCase()] ?? 'badge bg-secondary text-white';
        },

        async usageMonthChange(month) {
            this.$store.dispatch('admin/setMcpUsageMonth', month);
            this.$store.dispatch('admin/setMcpUsageDataStartDate', getFirstDayOfMonth(month));
            this.$store.dispatch('admin/setMcpUsageDataEndDate', getLastDayOfMonth(month));
            this.$emit('getUsageData', true);
        },
        async startDateChange(newValue) {
            if (newValue !== this.mcpStartDate) {
                this.$store.dispatch('admin/setMcpUsageMonth', 'custom');
                this.$store.dispatch('admin/setMcpUsageDataStartDate', newValue);
                this.$emit('getUsageData', true);
            }
        },
        async endDateChange(newValue) {
            if (newValue !== this.mcpEndDate) {
                this.$store.dispatch('admin/setMcpUsageMonth', 'custom');
                this.$store.dispatch('admin/setMcpUsageDataEndDate', newValue);
                this.$emit('getUsageData', true);
            }
        },
    },

    beforeMount() {
        this.$emit('getUsageData', false);
    },
};
</script>

<style scoped>
.chart-view {
    min-height: 380px;
    position: relative;
}

.agg-table-wrap {
    max-height: 520px;
    overflow-y: auto;
    border-radius: 4px;
}

.agg-table-wrap table {
    table-layout: fixed;
    width: 100%;
}

.agg-cell-truncate {
    max-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.subtotal-row td {
    background: #f8f9fa;
    border-top: 1px dashed #dee2e6;
}

/* Unattributed sits with the users but shouldn't compete with them */
.unattributed-row td {
    opacity: 0.75;
}
</style>